// store/modules/mediaExportJobs.js
//
// Owns the media (video/photo) export encode once MediaEditor.vue's "Terminer" is clicked, as a
// plain Vuex action rather than a promise living inside a Vue component instance - so it survives
// StandardTab.vue/MediaEditor.vue being destroyed by a route navigation (postCreator has no
// <keep-alive>, see PostCreator.vue). The encode functions themselves (exportTimelineToMp4/
// exportTimelineViaMediaRecorder/exportTimelineToImage) are already pure - only the component's
// $emit-based result delivery breaks on destroy, not the encode math itself.
//
// A second concept, pendingPublishes, lets PostCreator.vue defer its actual publish call until
// every export job a submitted post depends on has resolved (see sendPostAllPost).

import { exportTimelineToMp4 } from '@/components/dash/mediaEditor/engine/export/exportTimeline.js'
import { exportTimelineViaMediaRecorder } from '@/components/dash/mediaEditor/engine/export/mediaRecorderFallback.js'
import { exportTimelineToImage } from '@/components/dash/mediaEditor/engine/export/exportImage.js'
import { supportsWebCodecsExport } from '@/components/dash/mediaEditor/engine/codecs/capabilities.js'

let nextJobId = 1
let nextPublishId = 1

// startExport below never queued jobs against each other - every call kicks off its own fully
// independent async encode, so exporting two scopes around the same time (general + a page/
// network personalization, exactly the parallel-export capability this module exists for) ran
// their frame loops truly concurrently. Both share the single JS main thread and the browser's
// video decoder, and exportTimeline.js's advanceTo already documents the risk this creates on its
// own (see MAX_FRAME_WAITS/FAST_SCAN_RATE): its small forward steps let a <video> keep playing in
// real time while our own per-frame JS work runs, so any main-thread time spent servicing a
// SIBLING job's decode/step/canvas-draw/encode work between two of THIS job's frames is exactly
// the kind of stall that produces an overshot, wrongly-timed captured frame - visible as the same
// "scratch"/judder defect, just from a different contention source now that the old live-preview
// canvas (the original documented cause) is gone. Serializing the actual frame-loop work below
// (not the job bookkeeping - a queued job still flips to 'running' immediately, same as before)
// keeps every scope's export progressing independently from the user's point of view while
// guaranteeing only one is ever actually decoding/drawing/encoding on the main thread at a time.
let exportQueueTail = Promise.resolve()
const runExportExclusively = (fn) => {
    const result = exportQueueTail.then(fn, fn)
    // Never let one failed export poison the queue for every job after it.
    exportQueueTail = result.then(() => {}, () => {})
    return result
}

export default {
    namespaced: true,

    state: {
        jobs: {}, // jobId -> { scopeKey, kind: 'photo'|'video', status: 'running'|'done'|'failed', progress, resultFile, error }
        pendingPublishes: {} // publishId -> { jobIds, status, snapshot, draftId, posts, error }
    },

    mutations: {
        setJob(state, { jobId, job }) {
            state.jobs = { ...state.jobs, [jobId]: job }
        },
        patchJob(state, { jobId, patch }) {
            if (!state.jobs[jobId]) return
            state.jobs = { ...state.jobs, [jobId]: { ...state.jobs[jobId], ...patch } }
        },
        removeJob(state, { jobId }) {
            const jobs = { ...state.jobs }
            delete jobs[jobId]
            state.jobs = jobs
        },
        setPendingPublish(state, { publishId, entry }) {
            state.pendingPublishes = { ...state.pendingPublishes, [publishId]: entry }
        },
        patchPendingPublish(state, { publishId, patch }) {
            if (!state.pendingPublishes[publishId]) return
            state.pendingPublishes = { ...state.pendingPublishes, [publishId]: { ...state.pendingPublishes[publishId], ...patch } }
        },
        removePendingPublish(state, { publishId }) {
            const pendingPublishes = { ...state.pendingPublishes }
            delete pendingPublishes[publishId]
            state.pendingPublishes = pendingPublishes
        }
    },

    getters: {
        // Used by the planner/drawer to grey out edit/delete/drag&drop for a post whose media is
        // still being prepared (see posts/components/drawer/index.vue, posts/mixins/
        // plannerBehavior.js). Matches by page+post id against each pendingPublish's own `posts`
        // (Cas A: the safety draft's freshly-created posts; Cas B: the real dataEditPost.posts
        // already in the snapshot - see PostCreator.vue's sendPostAllPostDeferred/
        // createSafetyDraftForPendingPublish). A resolved (success or failure) pendingPublish is
        // removed via clearPendingPublish shortly after settling, so its mere presence here already
        // means "still being prepared" - no separate status check needed.
        isPostLocked: (state) => (postId, pageId) => {
            return Object.values(state.pendingPublishes).some((entry) => {
                return (entry.posts || []).some((p) => p.page == pageId && p._id == postId)
            })
        }
    },

    actions: {
        // Builds its own resolveSource from plain data (an origin URL string + raw extraFiles File
        // objects) rather than reusing MediaEditor.vue's own fileRegistry/resolveSource - a blob URL
        // stays resolvable for the document's whole lifetime unless explicitly revoked (component
        // destruction alone never revokes one), but MediaEditor.vue's own beforeDestroy DOES revoke
        // its extraFiles URLs - so this needs independent ones, immune to that.
        //
        // Deliberately NOT awaited before returning jobId - the encode itself runs detached (its
        // promise chain lives in this module, not in the dispatch call), so the caller
        // (MediaEditor.vue's finish()) gets jobId back immediately and can watch the store for
        // progress/completion instead of blocking on the whole export.
        startExport({ commit }, { scopeKey, kind, timeline, originUrl, originKind, extraFiles, atMs }) {
            const jobId = 'job-' + (nextJobId++)
            commit('setJob', { jobId, job: { scopeKey, kind, status: 'running', progress: 0, resultFile: null, error: null } })

            const ownedUrls = []
            const fileRegistry = { origin: { kind: originKind, url: originUrl } }
            Object.keys(extraFiles || {}).forEach((fileId) => {
                const file = extraFiles[fileId]
                const url = URL.createObjectURL(file)
                ownedUrls.push(url)
                const kind = file.type.startsWith('image/') ? 'image' : (file.type.startsWith('audio/') ? 'audio' : 'video')
                fileRegistry[fileId] = { kind, url, localFile: file }
            })
            const resolveSource = (fileId) => fileRegistry[fileId] || null

            ;(async () => {
                try {
                    let file
                    if (kind === 'photo') {
                        const blob = await runExportExclusively(() => exportTimelineToImage(timeline, { resolveSource, atMs }))
                        file = new File([blob], `image-edit-${Date.now()}.png`, { type: 'image/png' })
                    } else {
                        const useWebCodecs = await supportsWebCodecsExport()
                        const exportFn = useWebCodecs ? exportTimelineToMp4 : exportTimelineViaMediaRecorder
                        const blob = await runExportExclusively(() => exportFn(timeline, {
                            resolveSource,
                            onProgress: (p) => commit('patchJob', { jobId, patch: { progress: p } })
                        }))
                        const extension = blob.type.includes('mp4') ? 'mp4' : 'webm'
                        file = new File([blob], `video-edit-${Date.now()}.${extension}`, { type: blob.type })
                    }
                    // progress stays on the same 0-1 scale used throughout (onProgress callbacks
                    // above report a fraction, not a percentage) - setting 100 here previously made
                    // it jump from ~1 to 100 right at completion, which a consumer that multiplies by
                    // 100 for display (see PostCreator.vue's sendPostAllPostDeferred) briefly showed
                    // as 10000%.
                    commit('patchJob', { jobId, patch: { status: 'done', progress: 1, resultFile: file } })
                } catch (e) {
                    console.error('[mediaExportJobs] export failed:', e)
                    commit('patchJob', { jobId, patch: { status: 'failed', error: e } })
                } finally {
                    ownedUrls.forEach((url) => URL.revokeObjectURL(url))
                }
            })()

            return jobId
        },

        // Registers a post submission that depends on one or more still-running jobs. Resolves
        // (or rejects, carrying the failed job's error) once every referenced job settles - the
        // caller (PostCreator.vue's sendPostAllPost) awaits this before running the actual publish.
        registerPendingPublish({ commit, state }, { jobIds, snapshot, draftId, posts }) {
            const publishId = 'publish-' + (nextPublishId++)
            commit('setPendingPublish', { publishId, entry: { jobIds, status: 'waiting', snapshot, draftId, posts: posts || [], error: null } })

            const waitForJob = (jobId) => new Promise((resolve, reject) => {
                const check = () => {
                    const job = state.jobs[jobId]
                    if (!job) return reject(new Error('Job d’export introuvable : ' + jobId))
                    if (job.status === 'done') return resolve(job.resultFile)
                    if (job.status === 'failed') return reject(job.error || new Error('Export échoué'))
                    setTimeout(check, 250)
                }
                check()
            })

            const resultPromise = Promise.all(jobIds.map(waitForJob))
                .then((resultFiles) => {
                    commit('patchPendingPublish', { publishId, patch: { status: 'ready' } })
                    return resultFiles
                })
                .catch((error) => {
                    commit('patchPendingPublish', { publishId, patch: { status: 'failed', error } })
                    throw error
                })

            return { publishId, resultPromise }
        },

        // Clears a job/pendingPublish once its result has been applied (or its failure
        // acknowledged) - jobs/pendingPublishes hold File/Blob references, so nothing here should
        // outlive being displayed/consumed.
        clearPendingPublish({ commit, state }, { publishId }) {
            const entry = state.pendingPublishes[publishId]
            if (entry) entry.jobIds.forEach((jobId) => commit('removeJob', { jobId }))
            commit('removePendingPublish', { publishId })
        }
    }
}
